import { useState } from "react";
import { BiEnvelope, BiMapPin, BiPhone } from "react-icons/bi";
import { buildMapEmbedUrl } from "../../data/branchData";
import { branchImageUrls, getBranchImage } from "../../data/branchImages";
import useBranches from "../../hooks/useBranches";

const formatTel = (phone) => phone.replace(/[^\d+]/g, "");

const getMapEmbedUrl = (branch) => {
  const url = (branch.mapEmbedUrl || "").trim();
  if (
    url.includes("google.com/maps") &&
    (url.includes("output=embed") || url.includes("/maps/embed"))
  ) {
    return url;
  }
  const address = [branch.addressLine1, branch.addressLine2]
    .filter(Boolean)
    .join(", ");
  return buildMapEmbedUrl(address);
};

const BranchCard = ({ branch }) => {
  const [imgSrc, setImgSrc] = useState(() =>
    getBranchImage(branch.id, branch.image)
  );
  const fallback = branchImageUrls[branch.id] || branchImageUrls.istanbul;

  return (
  <article
    id={`sube-${branch.id}`}
    className="flex flex-col h-full overflow-hidden bg-white dark:bg-card-dark border border-gray-200 dark:border-gray-700 rounded-xl shadow-md"
  >
    <img
      src={imgSrc}
      alt={`${branch.name} şubesi`}
      className="w-full h-40 object-cover"
      loading="lazy"
      onError={() => {
        if (imgSrc !== fallback) setImgSrc(fallback);
      }}
    />

    <div className="p-5 flex flex-col flex-1">
      <h3 className="text-xl font-bold text-center text-[#674921] dark:text-primary mb-4 pb-3 border-b border-gray-100 dark:border-gray-700">
        {branch.name} Şubesi
      </h3>

      <dl className="space-y-3 text-sm flex-1">
        {branch.phone ? (
          <div className="flex gap-2">
            <BiPhone className="text-primary shrink-0 mt-0.5" />
            <dd>
              <a
                href={`tel:${formatTel(branch.phone)}`}
                className="text-primary hover:underline"
              >
                {branch.phone}
              </a>
            </dd>
          </div>
        ) : null}
        {branch.email ? (
          <div className="flex gap-2">
            <BiEnvelope className="text-primary shrink-0 mt-0.5" />
            <dd>
              <a
                href={`mailto:${branch.email}`}
                className="text-primary hover:underline break-all normal-case"
              >
                {branch.email}
              </a>
            </dd>
          </div>
        ) : null}
        <div className="flex gap-2">
          <BiMapPin className="text-primary shrink-0 mt-0.5" />
          <dd className="text-muted">
            {branch.addressLine1}
            <br />
            {branch.addressLine2}
          </dd>
        </div>
      </dl>

      {branch.addressLine1 || branch.addressLine2 ? (
        <div className="mt-4 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 h-[200px]">
          <iframe
            src={getMapEmbedUrl(branch)}
            width="100%"
            height="200"
            style={{ border: 0, display: "block" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title={`${branch.name} konum haritası`}
            allowFullScreen
          />
        </div>
      ) : null}
      {branch.directionsUrl ? (
        <a
          href={branch.directionsUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-block mt-2 text-xs font-semibold text-primary"
        >
          Haritada aç →
        </a>
      ) : null}
    </div>
  </article>
  );
};

const BranchSkeleton = () => (
  <div className="h-[480px] rounded-xl bg-gray-100 dark:bg-dark-light animate-pulse" />
);

const BranchesSection = () => {
  const { data: branches, isLoading, isError, refetch } = useBranches();

  return (
    <section
      id="subelerimiz"
      className="scroll-mt-28 pt-12 pb-16 border-t border-gray-200 dark:border-gray-700"
      aria-label="Şube iletişim bilgileri"
    >
      <div className="text-center mb-10">
        <span className="sub-heading mx-auto">İletişim</span>
        <h2 className="heading">Şubelerimiz</h2>
      </div>

      {isLoading ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <BranchSkeleton key={i} />
          ))}
        </div>
      ) : null}

      {isError ? (
        <div className="text-center py-8">
          <p className="text-muted mb-4">Şube bilgileri yüklenemedi.</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="btn-primary px-6 py-2 rounded-lg text-sm"
          >
            Tekrar dene
          </button>
        </div>
      ) : null}

      {!isLoading && !isError && branches.length > 0 ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {branches.map((branch) => (
            <BranchCard key={branch.dbId ?? branch.id} branch={branch} />
          ))}
        </div>
      ) : null}

      {!isLoading && !isError && branches.length === 0 ? (
        <p className="text-center text-muted">Henüz şube bilgisi eklenmemiş.</p>
      ) : null}
    </section>
  );
};

export default BranchesSection;
